import { FileGenerationConnection, GeneratedFileStatus } from "@biggeo/bg-server-lib/studio";
import isEmpty from "lodash/isEmpty";

import compact from "lodash/compact";
import { RowType, TreeDataRow } from "../views/GeneratedFilesView";

export const getFileGenerationStatus = (
    status: GeneratedFileStatus[]
): GeneratedFileStatus => {
    // default status to failed when there are no children.
    if (!status || status.length === 0) {
        return GeneratedFileStatus.failed;
    }

    const allCompleted = status.every(
        (status) => status === GeneratedFileStatus.completed
    );
    const allFailed = status.every(
        (status) => status === GeneratedFileStatus.failed
    );
    const allQueued = status.every(
        (status) => status === GeneratedFileStatus.queued
    );
    const allExpired = status.every(
        (status) => status === GeneratedFileStatus.expired
    );
    const hasRunning = status.some(
        (status) => status === GeneratedFileStatus.running
    );
    const hasCompleted = status.some(
        (status) => status === GeneratedFileStatus.completed
    );
    const hasQueued = status.some(
        (status) => status === GeneratedFileStatus.queued
    );

    //complete cases:
    if (allCompleted) {
        return GeneratedFileStatus.completed;
    }

    if (
        hasCompleted &&
        status.every(
            (status) =>
                status === GeneratedFileStatus.completed ||
                status === GeneratedFileStatus.failed ||
                status === GeneratedFileStatus.expired
        )
    ) {
        return GeneratedFileStatus.completed;
    }

    //failed cases:
    if (allFailed) {
        return GeneratedFileStatus.failed;
    }

    //expired cases:
    if (allExpired) {
        return GeneratedFileStatus.expired;
    }

    // running cases
    if (hasRunning) {
        return GeneratedFileStatus.running;
    }

    //queued cases:
    if (allQueued) {
        return GeneratedFileStatus.queued;
    }

    if (hasQueued) {
        return GeneratedFileStatus.queued;
    }

    // Fallback:  This should ideally not be reached, but handles unexpected cases.
    return status[0];
};



export const getFileGenerationLastExportedAt = (
    arr: (Date | undefined | null)[]
): Date | undefined => {
    if (!arr || isEmpty(arr)) {
        return undefined;
    }
    const compactedArr = compact(arr);
    if (isEmpty(compactedArr)) {
        return undefined;
    }

    return compactedArr.reduce((latest, current) =>
        current > latest ? current : latest
    );
};

export const formatTreeData = (data: readonly FileGenerationConnection[]): TreeDataRow[] => {
    let result: TreeDataRow[] = [];
    data?.map(d => {
        const parentRow = {
            id: d.fileGeneration.id,
            source: compact(d.generatedFiles?.map(gf => gf.originCloudStorageType)) || [],
            name: d.fileGeneration.name,
            description: d.fileGeneration.description || undefined,
            savedViewId: d.fileGeneration.fkSavedViewId,
            mapTemplateId: d.mapTemplateId,
            _type: RowType.parent,
            fileCount: d.generatedFiles.length,
            status: getFileGenerationStatus(d.generatedFiles.map(f => f.generateFile.status)),
            lastExportedAt: getFileGenerationLastExportedAt(d.generatedFiles.map(f => f.generateFile.lastExportedAt)),
        };
        result.push(parentRow);

        d.generatedFiles?.map(gf => {
            const childRow = {
                id: `${parentRow.id}/${gf.generateFile.id}`,
                source: gf.originCloudStorageType ? [gf.originCloudStorageType] : [],
                name: gf.generateFile.name,
                status: gf.generateFile.status,
                size: gf.generateFile.diskActual || undefined,
                generatedFileId: gf.generateFile.id,
                fileCount: 1,
                _type: RowType.child,
                lastExportedAt: gf.generateFile.lastExportedAt || undefined,
            };
            result.push(childRow);
        })
    })
    return result;
}


export const isParentRow = (row: TreeDataRow): boolean => row._type === RowType.parent;

export const isChildRow = (row: TreeDataRow): boolean => row._type === RowType.child;
